// "Edit Purchase Request" side drawer for a request waiting to be delivered to
// its Balagruha (status 'delivered_store'). Every field is shown read-only; the
// requester (or an admin) can only move the status on to 'delivered_balagruha',
// the one move the backend allows them at this step.
import React, { useState } from 'react';
import { X, Info } from 'lucide-react';
import { updatePurchaseRequestStatus } from '../../../api';
import showToast from '../../../utils/toast';
import './UpdatePurchaseStatusModal.css';

const STATUS_CHOICES = [
    { value: 'delivered_store', label: 'Pending Deliveries to Balagruha' },
    { value: 'delivered_balagruha', label: 'Delivered to Balagruha' },
];

const PRIORITY_STYLE = {
    high: { label: 'High', color: '#dc2626', background: '#fee2e2' },
    medium: { label: 'Medium', color: '#b45309', background: '#fef3c7' },
    low: { label: 'Low', color: '#2563eb', background: '#dbeafe' },
};

const formatDate = (date) => {
    if (!date) return 'N/A';
    const parsed = new Date(date);
    return Number.isNaN(parsed.getTime()) ? 'N/A' : parsed.toLocaleDateString('en-IN');
};

const getBalagruhaName = (request, balagruhas) => {
    const id = (request.balagruhaId?._id || request.balagruhaId || '').toString();
    if (request.balagruhaId?.name) return request.balagruhaId.name;
    if (request.balagruhaName) return request.balagruhaName;
    if (id === 'STOCK') return 'Stock (General Inventory)';
    return balagruhas.find((bg) => bg._id === id)?.name || 'N/A';
};

const getItemNames = (items = []) =>
    items.map((entry) => entry.productName || entry.name || entry.shopItemId?.name).filter(Boolean).join(', ') || 'N/A';

const getTotalQuantity = (items = []) =>
    items.reduce((sum, entry) => sum + Number(entry.requestedQuantity || entry.quantity || 0), 0) || 'N/A';

const ReadOnlyField = ({ label, children }) => (
    <div className="ups-field">
        <span className="ups-label">{label}</span>
        <div className="ups-value">{children}</div>
    </div>
);

export default function UpdatePurchaseStatusModal({ request, balagruhas = [], onClose, onUpdated = () => {} }) {
    const [status, setStatus] = useState(request.status);
    const [submitting, setSubmitting] = useState(false);
    const priority = PRIORITY_STYLE[request.priority];
    const unchanged = status === request.status;

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (unchanged) return;

        try {
            setSubmitting(true);
            const response = await updatePurchaseRequestStatus(request._id, {
                status,
                notes: 'Marked Delivered to Balagruha by requester',
            });
            if (!response?.success) {
                showToast(response?.message || 'Failed to update status', 'error');
                return;
            }
            showToast('Marked as Delivered to Balagruha', 'success');
            onUpdated();
        } catch (error) {
            showToast(error.response?.data?.message || 'Failed to update status', 'error');
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="ups-overlay" onClick={onClose}>
            <aside
                className="ups-drawer"
                role="dialog"
                aria-modal="true"
                aria-label="Edit Purchase Request"
                onClick={(e) => e.stopPropagation()}
            >
                <div className="ups-header">
                    <h3>Edit Purchase Request</h3>
                    <button type="button" className="ups-close" onClick={onClose} aria-label="Close">
                        <X size={18} />
                    </button>
                </div>

                <form onSubmit={handleSubmit}>
                    <div className="ups-body">
                        <ReadOnlyField label="Balagruha Name">{getBalagruhaName(request, balagruhas)}</ReadOnlyField>
                        <ReadOnlyField label="Requested By">
                            {request.requestedBy?.name || request.requestedByName || 'N/A'}
                        </ReadOnlyField>
                        <ReadOnlyField label="Priority">
                            {priority ? (
                                <span className="ups-priority" style={{ color: priority.color, background: priority.background }}>
                                    <span className="ups-priority-dot" style={{ background: priority.color }} />
                                    {priority.label}
                                </span>
                            ) : (
                                'N/A'
                            )}
                        </ReadOnlyField>

                        <div className="ups-row">
                            <ReadOnlyField label="Date">{formatDate(request.createdAt)}</ReadOnlyField>
                            <ReadOnlyField label="Due Date">{formatDate(request.deadline)}</ReadOnlyField>
                        </div>

                        <ReadOnlyField label="Item Name">{getItemNames(request.items)}</ReadOnlyField>

                        <div className="ups-row">
                            <ReadOnlyField label="Category">{request.category || 'N/A'}</ReadOnlyField>
                            <ReadOnlyField label="Quantity">{getTotalQuantity(request.items)}</ReadOnlyField>
                        </div>

                        <ReadOnlyField label="Description">
                            <span className="ups-description">{request.justification || '—'}</span>
                        </ReadOnlyField>

                        <div className="ups-field">
                            <label className="ups-label" htmlFor="ups-status">
                                Status <span className="ups-required">*</span>
                            </label>
                            <select
                                id="ups-status"
                                className="ups-select"
                                value={status}
                                onChange={(e) => setStatus(e.target.value)}
                            >
                                {STATUS_CHOICES.map((choice) => (
                                    <option key={choice.value} value={choice.value}>{choice.label}</option>
                                ))}
                            </select>
                        </div>

                        <div className="ups-note">
                            <Info size={16} className="ups-note-icon" />
                            <span>
                                Only the status can be updated for items that are <strong>"Pending Deliveries to Balagruha"</strong>.
                                Other fields cannot be edited.
                            </span>
                        </div>
                    </div>

                    <div className="ups-footer">
                        <button type="button" className="ups-btn-cancel" onClick={onClose} disabled={submitting}>
                            Cancel
                        </button>
                        <button type="submit" className="ups-btn-save" disabled={submitting || unchanged}>
                            {submitting ? 'Saving...' : 'Save Changes'}
                        </button>
                    </div>
                </form>
            </aside>
        </div>
    );
}
